import { useState } from "react";
import {
  Package,
  Plus,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  Truck,
  Search,
  AlertCircle,
  RotateCcw,
  ThumbsUp,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Tooltip } from "@/components/ui/Tooltip";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useSupplyItems,
  useCreateSupplyItem,
  useApproveSupplyItem,
  useOrderSupplyItem,
  useRejectSupplyItem,
  useMarkDeliveredSupplyItem,
  useReopenSupplyItem,
} from "../hooks/useSupplies";
import { SUPPLY_STATUS_COLORS, type SupplyItem, type SupplyStatus } from "../types";

type StatusFilter = "all" | SupplyStatus;

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "pending", label: "Oczekujące" },
  { value: "approved", label: "Zaakceptowane" },
  { value: "ordered", label: "Zamówione" },
  { value: "rejected", label: "Odrzucone" },
];

const MANAGER_ROLES = ["director", "coordinator"];

export function SuppliesPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = !!user && MANAGER_ROLES.includes(user.role);
  const isDirector = user?.role === "director";

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [priorityFilter, setPriorityFilter] = useState<"all" | "urgent" | "standard">("all");
  const [search, setSearch] = useState("");

  const params: Record<string, string> = {};
  if (statusFilter !== "all") params.status = statusFilter;
  if (priorityFilter !== "all") params.priority = priorityFilter;
  if (search.trim()) params.search = search.trim();

  const { data: items = [], isLoading } = useSupplyItems(
    Object.keys(params).length > 0 ? params : undefined
  );

  const pendingCount = items.filter((i) => i.status === "pending").length;

  // ── Create modal ──────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    item_name: "",
    product_link: "",
    quantity: "",
    unit: "",
    priority: "standard" as "standard" | "urgent",
    delivery_location: "",
    preferred_delivery: "",
    notes: "",
    requested_at: "",
  });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const createItem = useCreateSupplyItem();

  const resetForm = () => {
    setForm({
      item_name: "",
      product_link: "",
      quantity: "",
      unit: "",
      priority: "standard",
      delivery_location: "",
      preferred_delivery: "",
      notes: "",
      requested_at: "",
    });
    setCreateErrors({});
  };

  const submitCreate = () => {
    const errors: Record<string, string> = {};
    if (!form.item_name.trim()) errors.item_name = "Nazwa towaru jest wymagana";
    if (!form.quantity.trim()) errors.quantity = "Ilość jest wymagana";
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }
    createItem.mutate(
      {
        item_name: form.item_name.trim(),
        product_link: form.product_link.trim(),
        quantity: form.quantity.trim(),
        unit: form.unit.trim(),
        priority: form.priority,
        delivery_location: form.delivery_location.trim(),
        preferred_delivery: form.preferred_delivery.trim(),
        notes: form.notes.trim(),
        requested_at: form.requested_at || undefined,
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          resetForm();
        },
        onError: () => {
          setCreateErrors({ submit: "Błąd podczas dodawania. Spróbuj ponownie." });
        },
      }
    );
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const approveItem = useApproveSupplyItem();

  // ── Order modal ───────────────────────────────────────────────────────────
  const [orderModal, setOrderModal] = useState<SupplyItem | null>(null);
  const [orderForm, setOrderForm] = useState({ ordered_at: "", delivery_info: "", notes: "" });
  const [orderError, setOrderError] = useState("");
  const orderItem = useOrderSupplyItem();

  const openOrderModal = (item: SupplyItem) => {
    setOrderModal(item);
    setOrderForm({ ordered_at: "", delivery_info: item.delivery_info, notes: item.notes });
    setOrderError("");
  };

  const submitOrder = () => {
    if (!orderModal) return;
    setOrderError("");
    orderItem.mutate(
      {
        id: orderModal.id,
        data: {
          ordered_at: orderForm.ordered_at || undefined,
          delivery_info: orderForm.delivery_info,
          notes: orderForm.notes,
        },
      },
      {
        onSuccess: () => setOrderModal(null),
        onError: () => setOrderError("Błąd podczas zamawiania. Spróbuj ponownie."),
      }
    );
  };

  // ── Reject modal ──────────────────────────────────────────────────────────
  const [rejectModal, setRejectModal] = useState<SupplyItem | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [rejectError, setRejectError] = useState("");
  const rejectItem = useRejectSupplyItem();

  const openRejectModal = (item: SupplyItem) => {
    setRejectModal(item);
    setRejectNotes(item.notes);
    setRejectError("");
  };

  const submitReject = () => {
    if (!rejectModal) return;
    setRejectError("");
    rejectItem.mutate(
      { id: rejectModal.id, data: { notes: rejectNotes } },
      {
        onSuccess: () => setRejectModal(null),
        onError: () => setRejectError("Błąd podczas odrzucania. Spróbuj ponownie."),
      }
    );
  };

  // ── Delivered modal ───────────────────────────────────────────────────────
  const [deliveredModal, setDeliveredModal] = useState<SupplyItem | null>(null);
  const [deliveredAt, setDeliveredAt] = useState("");
  const [deliveredError, setDeliveredError] = useState("");
  const markDelivered = useMarkDeliveredSupplyItem();

  const openDeliveredModal = (item: SupplyItem) => {
    setDeliveredModal(item);
    setDeliveredAt("");
    setDeliveredError("");
  };

  const submitDelivered = () => {
    if (!deliveredModal) return;
    setDeliveredError("");
    markDelivered.mutate(
      { id: deliveredModal.id, data: { delivered_at: deliveredAt || undefined } },
      {
        onSuccess: () => setDeliveredModal(null),
        onError: () => setDeliveredError("Błąd podczas zapisywania. Spróbuj ponownie."),
      }
    );
  };

  // ── Copy ─────────────────────────────────────────────────────────────────
  function handleCopy(item: SupplyItem) {
    setForm({
      item_name: item.item_name,
      product_link: item.product_link,
      quantity: item.quantity,
      unit: item.unit,
      priority: item.priority,
      delivery_location: item.delivery_location,
      preferred_delivery: item.preferred_delivery,
      notes: item.notes,
      requested_at: "",
    });
    setCreateErrors({});
    setCreateOpen(true);
  }

  // ── Reopen ────────────────────────────────────────────────────────────────
  const reopenItem = useReopenSupplyItem();

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-5">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Zaopatrzenie biurowe</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3 sm:mb-6" />
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Zgłaszaj i śledź zapotrzebowanie na materiały i artykuły biurowe.
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setCreateOpen(true); }}
          className="flex-shrink-0"
          aria-label="Nowe zgłoszenie"
        >
          <Plus size={15} className="sm:mr-1.5" />
          <span className="hidden sm:inline">Nowe zgłoszenie</span>
        </Button>
      </div>

      {/* Stats bar */}
      {pendingCount > 0 && statusFilter === "all" && (
        <div className="mb-4 flex items-start sm:items-center gap-2 text-xs sm:text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5">
          <AlertCircle size={15} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5 sm:mt-0" />
          <span className="text-yellow-800 dark:text-yellow-300 font-medium">
            {pendingCount} {pendingCount === 1 ? "pozycja oczekuje" : "pozycje oczekują"} na akceptację
            {isDirector ? " — do zatwierdzenia przez Ciebie" : ""}
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-3 mb-4 md:mb-5">
        {/* Status tabs — scroll horizontally on mobile */}
        <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide">
          <div className="inline-flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 whitespace-nowrap">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === tab.value
                    ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Priority + Search row on mobile, individual elements on desktop */}
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 md:contents">
          {/* Priority filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as typeof priorityFilter)}
            className="w-full sm:w-auto rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 md:py-1.5 text-sm text-gray-700 dark:text-gray-300"
          >
            <option value="all">Wszystkie priorytety</option>
            <option value="urgent">Pilne</option>
            <option value="standard">Standardowe</option>
          </select>

          {/* Search */}
          <div className="relative flex-1 md:min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj towaru..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 md:py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <PageLoader />
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-10 text-center">
          <Package size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak zgłoszeń w tej kategorii.</p>
          <button
            onClick={() => { resetForm(); setCreateOpen(true); }}
            className="mt-3 text-sm text-[var(--color-primary)] dark:text-blue-400 hover:underline"
          >
            Dodaj pierwsze zgłoszenie
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-hidden">
          {/* Mobile: card list */}
          <ul className="md:hidden flex flex-col gap-2">
            {items.map((item) => (
              <SupplyCard
                key={item.id}
                item={item}
                isManager={isManager}
                isDirector={isDirector}
                onApprove={(i) => approveItem.mutate(i.id)}
                onOrder={openOrderModal}
                onReject={openRejectModal}
                onMarkDelivered={openDeliveredModal}
                onReopen={(i) => reopenItem.mutate(i.id)}
                onCopy={handleCopy}
              />
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Towar</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość / Jdnst.</th>
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pilność</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zgłosił</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zatwierdził</th>
                  <th className="text-left px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Dostawa do</th>
                  <th className="text-left px-3 py-2 w-36 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                  <th className="text-left px-3 py-2 w-20 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jest</th>
                  <th className="text-left px-3 py-2 w-40 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((item) => (
                  <SupplyRow
                    key={item.id}
                    item={item}
                    isManager={isManager}
                    isDirector={isDirector}
                    onApprove={(i) => approveItem.mutate(i.id)}
                    onOrder={openOrderModal}
                    onReject={openRejectModal}
                    onMarkDelivered={openDeliveredModal}
                    onReopen={(i) => reopenItem.mutate(i.id)}
                    onCopy={handleCopy}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-1 md:px-4 py-2.5 md:border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
            {items.length} {items.length === 1 ? "pozycja" : "pozycji"}
          </div>
        </div>
      )}

      {/* ── Create modal ── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nowe zgłoszenie zapotrzebowania">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Nazwa towaru <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. Papier ksero A4, Toner czarny do Xerox..."
                value={form.item_name}
                onChange={(e) => setForm((f) => ({ ...f, item_name: e.target.value }))}
              />
              {createErrors.item_name && (
                <p className="text-xs text-red-500 mt-1">{createErrors.item_name}</p>
              )}
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Link / Źródło <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="URL lub opis sklepu (np. Allegro, Artim, jak zawsze...)"
                value={form.product_link}
                onChange={(e) => setForm((f) => ({ ...f, product_link: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Ilość <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. 3, x5, jak zawsze"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
              {createErrors.quantity && (
                <p className="text-xs text-red-500 mt-1">{createErrors.quantity}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Jednostka <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. sztuki, opakowania, ryza"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Pilność
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: e.target.value as "standard" | "urgent" }))
                }
              >
                <option value="standard">Standardowe</option>
                <option value="urgent">PILNE</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Miejsce dostawy <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. Opole, Wrocław, Opole biuro"
                value={form.delivery_location}
                onChange={(e) => setForm((f) => ({ ...f, delivery_location: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Preferowana dostawa <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. kurier, Artim, paczkomat, odbiór osobisty"
                value={form.preferred_delivery}
                onChange={(e) => setForm((f) => ({ ...f, preferred_delivery: e.target.value }))}
              />
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="Dodatkowe informacje, model produktu, zastrzeżenia..."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>

          {createErrors.submit && (
            <p className="text-sm text-red-600">{createErrors.submit}</p>
          )}

          <div className="flex gap-3">
            <Button loading={createItem.isPending} onClick={submitCreate}>
              Dodaj zgłoszenie
            </Button>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Order modal ── */}
      <Modal open={!!orderModal} onClose={() => setOrderModal(null)} title="Zamów pozycję">
        {orderModal && (
          <div className="flex flex-col gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-[var(--color-primary)] dark:text-blue-200">#{orderModal.number} {orderModal.item_name}</p>
              <p className="text-[var(--color-primary)] dark:text-blue-400 text-xs mt-0.5">
                {orderModal.quantity} {orderModal.unit} · {orderModal.requested_by_name ?? "—"}
              </p>
              {orderModal.approved_by_name && (
                <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                  Zaakceptowane przez: {orderModal.approved_by_name}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data zamówienia <span className="text-gray-400 text-xs">(domyślnie: dziś)</span>
              </label>
              <DatePicker
                value={orderForm.ordered_at}
                onChange={(v) => setOrderForm((f) => ({ ...f, ordered_at: v }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Sposób dostawy <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <input
                type="text"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="np. kurier, Artim, serwisant, paczkomat"
                value={orderForm.delivery_info}
                onChange={(e) => setOrderForm((f) => ({ ...f, delivery_info: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={orderForm.notes}
                onChange={(e) => setOrderForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>

            {orderError && <p className="text-sm text-red-600">{orderError}</p>}

            <div className="flex gap-3">
              <Button loading={orderItem.isPending} onClick={submitOrder}>
                <CheckCircle2 size={14} className="mr-1.5" /> Zamów
              </Button>
              <Button variant="secondary" onClick={() => setOrderModal(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Reject modal ── */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć zgłoszenie">
        {rejectModal && (
          <div className="flex flex-col gap-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-red-900 dark:text-red-200">{rejectModal.item_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {rejectModal.requested_by_name ?? "—"}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Uwagi / Powód odrzucenia <span className="text-gray-400 text-xs">(opcjonalnie)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                placeholder="Powód odrzucenia..."
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
              />
            </div>

            {rejectError && <p className="text-sm text-red-600">{rejectError}</p>}

            <div className="flex gap-3">
              <Button loading={rejectItem.isPending} onClick={submitReject} variant="secondary">
                <XCircle size={14} className="mr-1.5" /> Odrzuć
              </Button>
              <Button variant="secondary" onClick={() => setRejectModal(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Mark delivered modal ── */}
      <Modal open={!!deliveredModal} onClose={() => setDeliveredModal(null)} title="Potwierdź dostawę">
        {deliveredModal && (
          <div className="flex flex-col gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold text-green-900 dark:text-green-200">{deliveredModal.item_name}</p>
              <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                {deliveredModal.quantity} {deliveredModal.unit}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data dostawy <span className="text-gray-400 text-xs">(domyślnie: dziś)</span>
              </label>
              <DatePicker
                value={deliveredAt}
                onChange={setDeliveredAt}
              />
            </div>

            {deliveredError && <p className="text-sm text-red-600">{deliveredError}</p>}

            <div className="flex gap-3">
              <Button loading={markDelivered.isPending} onClick={submitDelivered}>
                <Truck size={14} className="mr-1.5" /> Potwierdź dostawę
              </Button>
              <Button variant="secondary" onClick={() => setDeliveredModal(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Table row ─────────────────────────────────────────────────────────────────

interface SupplyRowProps {
  item: SupplyItem;
  isManager: boolean;
  isDirector: boolean;
  onApprove: (item: SupplyItem) => void;
  onOrder: (item: SupplyItem) => void;
  onReject: (item: SupplyItem) => void;
  onMarkDelivered: (item: SupplyItem) => void;
  onReopen: (item: SupplyItem) => void;
  onCopy: (item: SupplyItem) => void;
}

function SupplyRow({
  item,
  isManager,
  isDirector,
  onApprove,
  onOrder,
  onReject,
  onMarkDelivered,
  onReopen,
  onCopy,
}: SupplyRowProps) {
  const isUrgent = item.priority === "urgent";
  const isPending = item.status === "pending";

  const StatusIcon = () => {
    if (item.status === "approved") return <CheckCircle2 size={12} className="text-green-500" />;
    if (item.status === "ordered") return <CheckCircle2 size={12} className="text-[var(--color-primary)]" />;
    if (item.status === "rejected") return <XCircle size={12} className="text-red-500" />;
    return <Clock size={12} className="text-yellow-500" />;
  };

  return (
    <tr
      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors border-l-4 ${
        isPending
          ? "border-yellow-400 dark:border-yellow-500"
          : "border-transparent"
      } ${isUrgent ? "bg-red-50/30 dark:bg-red-900/10" : ""}`}
    >
      {/* Date */}
      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(item.requested_at || item.created_at)}
      </td>

      {/* Product */}
      <td className="px-3 py-2.5 max-w-xs">
        <div className="flex items-start gap-1.5">
          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug line-clamp-2">
            {item.item_name}
          </span>
          {item.product_link && (
            <a
              href={item.product_link.startsWith("http") ? item.product_link : undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={item.product_link}
              className="flex-shrink-0 text-[var(--color-primary)] hover:text-[var(--color-primary)] mt-0.5"
              onClick={(e) => {
                if (!item.product_link.startsWith("http")) e.preventDefault();
              }}
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
        {item.notes && (
          <Tooltip content={item.notes} onlyWhenTruncated className="block mt-0.5">
            <span className="text-xs text-gray-400 dark:text-gray-500 line-clamp-1 italic">{item.notes}</span>
          </Tooltip>
        )}
      </td>

      {/* Quantity / Unit */}
      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
        {item.quantity}
        {item.unit && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{item.unit}</span>
        )}
      </td>

      {/* Priority */}
      <td className="px-3 py-2.5">
        {isUrgent ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
            <AlertCircle size={10} /> PILNE
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">standardowe</span>
        )}
      </td>

      {/* Requested by */}
      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {item.requested_by_name ?? "—"}
      </td>

      {/* Approved by */}
      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {item.approved_by_name ? (
          <span className="text-green-700 dark:text-green-400">{item.approved_by_name}</span>
        ) : (
          <span className="text-gray-300 dark:text-gray-600">—</span>
        )}
      </td>

      {/* Delivery location */}
      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400">
        {item.delivery_location || "—"}
        {item.preferred_delivery && (
          <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5 italic">{item.preferred_delivery}</p>
        )}
      </td>

      {/* Status */}
      <td className="px-3 py-2.5">
        <Badge color={SUPPLY_STATUS_COLORS[item.status]}>
          <StatusIcon />
          <span className="ml-1">{item.status_display}</span>
        </Badge>
      </td>

      {/* Ordered at */}
      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(item.ordered_at)}
        {item.delivery_info && (
          <p className="text-gray-400 dark:text-gray-500 text-xs">{item.delivery_info}</p>
        )}
      </td>

      {/* Delivered */}
      <td className="px-3 py-2.5 text-center">
        {item.is_delivered ? (
          <span title={item.delivered_at ? formatDate(item.delivered_at) : undefined} className="inline-flex items-center justify-center text-green-600 dark:text-green-400">
            <CheckCircle2 size={16} />
          </span>
        ) : (
          <span className="text-gray-300 dark:text-gray-600">—</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isManager && (
            <>
              {/* pending: only director can approve/reject */}
              {item.status === "pending" && isDirector && (
                <>
                  <button
                    onClick={() => onApprove(item)}
                    className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 font-medium whitespace-nowrap flex items-center gap-1"
                  >
                    <ThumbsUp size={11} /> Zatwierdź
                  </button>
                  <button
                    onClick={() => onReject(item)}
                    className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                  >
                    Odrzuć
                  </button>
                </>
              )}

              {/* approved: manager can order; director can also reject */}
              {item.status === "approved" && (
                <>
                  <button
                    onClick={() => onOrder(item)}
                    className="text-xs bg-[var(--color-primary)] text-white px-2 py-1 rounded hover:bg-[var(--color-primary-hover)] font-medium whitespace-nowrap"
                  >
                    Zamów
                  </button>
                  {isDirector && (
                    <button
                      onClick={() => onReject(item)}
                      className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                    >
                      Odrzuć
                    </button>
                  )}
                </>
              )}

              {/* ordered: mark delivered */}
              {item.status === "ordered" && !item.is_delivered && (
                <button
                  onClick={() => onMarkDelivered(item)}
                  className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  <Truck size={11} /> Dostarczone
                </button>
              )}

              {/* rejected: reopen */}
              {item.status === "rejected" && (
                <button
                  onClick={() => onReopen(item)}
                  className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-50 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  <RotateCcw size={11} /> Przywróć
                </button>
              )}
            </>
          )}

          <button
            onClick={() => onCopy(item)}
            title="Kopiuj zgłoszenie"
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Copy size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────

function SupplyCard({
  item,
  isManager,
  isDirector,
  onApprove,
  onOrder,
  onReject,
  onMarkDelivered,
  onReopen,
  onCopy,
}: SupplyRowProps) {
  const isUrgent = item.priority === "urgent";
  const isPending = item.status === "pending";

  const StatusIcon = () => {
    if (item.status === "approved") return <CheckCircle2 size={12} className="text-green-500" />;
    if (item.status === "ordered") return <CheckCircle2 size={12} className="text-[var(--color-primary)]" />;
    if (item.status === "rejected") return <XCircle size={12} className="text-red-500" />;
    return <Clock size={12} className="text-yellow-500" />;
  };

  const requestedDate = (item.requested_at ?? item.created_at)
    ? formatDate(item.requested_at ?? item.created_at)
    : null;
  const orderedDate = item.ordered_at ? formatDate(item.ordered_at) : null;

  const hasActions =
    isManager &&
    ((item.status === "pending" && isDirector) ||
      item.status === "approved" ||
      (item.status === "ordered" && !item.is_delivered) ||
      item.status === "rejected");

  return (
    <li
      className={`relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm border-l-4 ${
        isPending ? "border-l-yellow-400 dark:border-l-yellow-500" : "border-l-gray-200 dark:border-l-gray-700"
      } ${isUrgent ? "bg-red-50/40 dark:bg-red-900/10" : ""}`}
    >
      {/* Top row: status + priority */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Badge color={SUPPLY_STATUS_COLORS[item.status]}>
            <StatusIcon />
            <span className="ml-1">{item.status_display}</span>
          </Badge>
        </div>
        {isUrgent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full whitespace-nowrap">
            <AlertCircle size={10} /> PILNE
          </span>
        )}
      </div>

      {/* Item name + link */}
      <div className="flex items-start gap-1.5 mb-1">
        <span className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug flex-1 break-words">
          {item.item_name}
        </span>
        {item.product_link && (
          <a
            href={item.product_link.startsWith("http") ? item.product_link : undefined}
            target="_blank"
            rel="noopener noreferrer"
            title={item.product_link}
            className="flex-shrink-0 text-[var(--color-primary)] mt-1"
            onClick={(e) => {
              if (!item.product_link.startsWith("http")) e.preventDefault();
            }}
          >
            <ExternalLink size={14} />
          </a>
        )}
      </div>

      {/* Notes */}
      {item.notes && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-1.5 line-clamp-2">{item.notes}</p>
      )}

      {/* Quantity · requester */}
      <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 mb-2">
        <span className="font-medium">
          {item.quantity}
          {item.unit && <span className="text-gray-400 dark:text-gray-500 ml-1 font-normal">{item.unit}</span>}
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="text-gray-600 dark:text-gray-400 truncate">{item.requested_by_name ?? "—"}</span>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
        {requestedDate && <span>📅 {requestedDate}</span>}
        {item.delivery_location && <span>📍 {item.delivery_location}</span>}
        {item.preferred_delivery && (
          <span className="italic text-gray-400 dark:text-gray-500">{item.preferred_delivery}</span>
        )}
        {item.approved_by_name && (
          <span className="text-green-700 dark:text-green-400">✓ {item.approved_by_name}</span>
        )}
        {orderedDate && (
          <span>
            Zam: {orderedDate}
            {item.delivery_info && <span className="text-gray-400 dark:text-gray-500"> · {item.delivery_info}</span>}
          </span>
        )}
        {item.is_delivered && (
          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
            <CheckCircle2 size={12} /> Dostarczone
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        {hasActions && (
          <>
            {item.status === "pending" && isDirector && (
              <>
                <button
                  onClick={() => onApprove(item)}
                  className="flex-1 text-sm bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 font-medium flex items-center justify-center gap-1.5"
                >
                  <ThumbsUp size={13} /> Zatwierdź
                </button>
                <button
                  onClick={() => onReject(item)}
                  className="flex-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                >
                  Odrzuć
                </button>
              </>
            )}

            {item.status === "approved" && (
              <>
                <button
                  onClick={() => onOrder(item)}
                  className="flex-1 text-sm bg-[var(--color-primary)] text-white px-3 py-2 rounded hover:bg-[var(--color-primary-hover)] font-medium"
                >
                  Zamów
                </button>
                {isDirector && (
                  <button
                    onClick={() => onReject(item)}
                    className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                  >
                    Odrzuć
                  </button>
                )}
              </>
            )}

            {item.status === "ordered" && !item.is_delivered && (
              <button
                onClick={() => onMarkDelivered(item)}
                className="flex-1 text-sm bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 font-medium flex items-center justify-center gap-1.5"
              >
                <Truck size={13} /> Dostarczone
              </button>
            )}

            {item.status === "rejected" && (
              <button
                onClick={() => onReopen(item)}
                className="flex-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium flex items-center justify-center gap-1.5"
              >
                <RotateCcw size={13} /> Przywróć
              </button>
            )}
          </>
        )}

        <button
          onClick={() => onCopy(item)}
          title="Kopiuj zgłoszenie"
          className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-gray-600 font-medium flex items-center gap-1.5"
        >
          <Copy size={13} /> Kopiuj
        </button>
      </div>
    </li>
  );
}
